import { useState, useEffect } from 'react';
import { X, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { supabase } from '../../integrations/supabase/client';
import { useToast } from '../../hooks/use-toast';

interface ContratoGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
  processoId: string;
  tipoProcesso: 'venda' | 'locacao' | 'prestacao_servico';
  onGenerated: () => void;
}

interface Template {
  id: string;
  nome: string;
  tipo: string;
  variaveis?: any;
}

export default function ContratoGenerator({ 
  isOpen, 
  onClose, 
  processoId, 
  tipoProcesso,
  onGenerated 
}: ContratoGeneratorProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [tipoContratoSelecionado, setTipoContratoSelecionado] = useState<string>(tipoProcesso);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
      setTipoContratoSelecionado(tipoProcesso);
    }
  }, [isOpen, tipoProcesso]);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [tipoContratoSelecionado]);

  const loadTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('templates_contrato')
        .select('*')
        .eq('tipo', tipoContratoSelecionado)
        .eq('ativo', true)
        .order('nome');

      if (error) throw error;
      setTemplates(data || []);
      
      if (data && data.length > 0) {
        setSelectedTemplateId(data[0].id);
      }
    } catch (error: any) {
      console.error('Erro ao carregar templates:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os templates',
        variant: 'destructive'
      });
    }
  };

  const handleGenerate = async () => {
    if (!selectedTemplateId) {
      toast({
        title: 'Atenção',
        description: 'Selecione um template',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.functions.invoke('generate-contrato', {
        body: {
          processo_id: processoId,
          template_id: selectedTemplateId,
          tipo_contrato: tipoContratoSelecionado
        }
      });

      if (error) throw error;

      const tipoNome = tipoContratoSelecionado === 'venda' ? 'Venda' : 
                       tipoContratoSelecionado === 'locacao' ? 'Locação' : 
                       'Prestação de Serviço';

      toast({
        title: 'Sucesso',
        description: `Contrato de ${tipoNome} gerado com sucesso!`
      });

      onGenerated();
      onClose();
    } catch (error: any) {
      console.error('Erro ao gerar contrato:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Não foi possível gerar o contrato',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-lg">
          <h2 className="text-xl font-semibold">Gerar Contrato</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Contrato *
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="tipoContrato"
                  value={tipoProcesso}
                  checked={tipoContratoSelecionado === tipoProcesso}
                  onChange={(e) => setTipoContratoSelecionado(e.target.value)}
                  className="w-4 h-4 text-orange-500"
                />
                <span className="font-medium">
                  Contrato de {tipoProcesso === 'venda' ? 'Venda' : 'Locação'} 
                  <span className="ml-2 text-xs text-gray-500">(Principal)</span>
                </span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="tipoContrato"
                  value="prestacao_servico"
                  checked={tipoContratoSelecionado === 'prestacao_servico'}
                  onChange={(e) => setTipoContratoSelecionado(e.target.value)}
                  className="w-4 h-4 text-orange-500"
                />
                <span className="font-medium">
                  Contrato de Prestação de Serviço
                  <span className="ml-2 text-xs text-gray-500">(Adicional)</span>
                </span>
              </label>
            </div>
          </div>

          <div>
            <label htmlFor="template" className="block text-sm font-medium text-gray-700 mb-1">
              Selecione o Template *
            </label>
            <select
              id="template"
              required
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              className="w-full mt-1 rounded-md border border-gray-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              {templates.length === 0 ? (
                <option value="">Nenhum template disponível</option>
              ) : (
                templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.nome}
                  </option>
                ))
              )}
            </select>
          </div>

          {templates.length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
              <FileText className="h-4 w-4 inline-block mr-2" />
              Não há templates ativos para {
                tipoContratoSelecionado === 'venda' ? 'venda' : 
                tipoContratoSelecionado === 'locacao' ? 'locação' : 
                'prestação de serviço'
              }. Crie um template nas configurações primeiro.
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
            <p className="font-medium mb-2">Atenção:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Certifique-se de que todas as partes foram cadastradas</li>
              <li>O contrato será gerado como rascunho para revisão</li>
              <li>Você poderá editar o conteúdo antes de enviar para aprovação</li>
            </ul>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              onClick={handleGenerate} 
              disabled={loading || templates.length === 0}
            >
              <FileText className="mr-2 h-4 w-4" />
              {loading ? 'Gerando...' : 'Gerar Contrato'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
